import { useEffect, useState, useMemo } from 'react';

/**
 * Automatic sizing for the spread matrix grids.
 *
 * Every size in the matrix used to be a hardcoded absolute: 65-72px cells and
 * 0.5-0.65rem fonts against a root font-size that is never overridden. On a
 * small laptop that overflows; on a large or high-DPI display it is needlessly
 * tiny. This derives one scale factor from the viewport and pixel ratio, and
 * exposes every metric already scaled, so both grid components stay in step.
 *
 * Scaling is fully automatic — there is no user control and nothing persisted.
 */

// Base metrics at scale 1.0, sized so a cell's three lines — the (p5,p50,p95)
// tuple, the headline value, and the percentile — all fit without clipping.
const BASE = {
  cellW: 76,
  cellH: 46,
  rowHeaderW: 74,
  summaryW: 52,
  headerH: 40,
  fontMain: 10.5,
  fontSub: 8.5,
  fontHeader: 10,
  cellPad: 2,
};

const MIN_SCALE = 0.9;
const MAX_SCALE = 1.6;

// Viewport width the base metrics were tuned against.
const REFERENCE_WIDTH = 1600;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Pick a scale for the current display.
 *
 * Two effects combined:
 *  - wider viewports get proportionally larger cells, so a 4K screen is not
 *    just the same tiny grid with more columns;
 *  - a high devicePixelRatio means CSS px are physically smaller, so text at a
 *    fixed px size reads smaller — nudge up to compensate.
 * Damped (square root / half weight) so neither effect runs away, then clamped.
 */
function computeScale(width, height, dpr) {
  if (!width) return 1;

  const widthScale = Math.sqrt(width / REFERENCE_WIDTH);

  // Only correct for genuinely dense displays; 1x and 2x-with-OS-scaling are
  // already handled by the browser's CSS pixel.
  const dprScale = dpr > 1.25 ? 1 + (Math.min(dpr, 3) - 1) * 0.12 : 1;

  // Short viewports (laptops, split boxes) should not inflate row height.
  const heightScale = height && height < 800 ? 0.92 : 1;

  return clamp(widthScale * dprScale * heightScale, MIN_SCALE, MAX_SCALE);
}

function readViewport() {
  if (typeof window === 'undefined') {
    return { width: REFERENCE_WIDTH, height: 900, dpr: 1 };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    dpr: window.devicePixelRatio || 1,
  };
}

/**
 * @returns {{scale:number, cellW:number, cellH:number, rowHeaderW:number,
 *            summaryW:number, headerH:number, fontMain:number, fontSub:number,
 *            fontHeader:number, stickyColsW:number, cssVars:object}}
 *   Numeric metrics (react-window needs real numbers) plus `cssVars`, a style
 *   object of custom properties for the Emotion-styled table variant.
 */
export default function useMatrixScale() {
  const [viewport, setViewport] = useState(readViewport);

  useEffect(() => {
    let frame = null;
    const onResize = () => {
      // Coalesce resize bursts into one update per frame.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setViewport(prev => {
          const next = readViewport();
          return (prev.width === next.width && prev.height === next.height && prev.dpr === next.dpr)
            ? prev
            : next;
        });
      });
    };

    window.addEventListener('resize', onResize);

    // devicePixelRatio changes when the window moves between monitors or the
    // browser zoom changes; resize alone does not always fire for that.
    const mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    mq.addEventListener?.('change', onResize);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      mq.removeEventListener?.('change', onResize);
    };
  }, []);

  return useMemo(() => {
    const scale = computeScale(viewport.width, viewport.height, viewport.dpr);
    const px = (base) => Math.round(base * scale);

    const m = {
      scale,
      cellW: px(BASE.cellW),
      cellH: px(BASE.cellH),
      rowHeaderW: px(BASE.rowHeaderW),
      summaryW: px(BASE.summaryW),
      headerH: px(BASE.headerH),
      // Fonts keep a decimal — rounding these to whole px is visibly coarse.
      fontMain: +(BASE.fontMain * scale).toFixed(2),
      fontSub: +(BASE.fontSub * scale).toFixed(2),
      fontHeader: +(BASE.fontHeader * scale).toFixed(2),
    };

    m.cellPad = Math.max(2, Math.round(BASE.cellPad * scale));
    // Ticker + seven summary columns: Price, Strip, Med%, ΔMed, Med Z, Cheap bp, Rank.
    m.stickyColsW = m.rowHeaderW + m.summaryW * 7;

    // Consumed by SpreadMatrix's styled() components, which read var(--mx-*)
    // so their Emotion class names stay stable as the scale changes.
    m.cssVars = {
      '--mx-cell-w': `${m.cellW}px`,
      '--mx-cell-h': `${m.cellH}px`,
      '--mx-row-header-w': `${m.rowHeaderW}px`,
      '--mx-summary-w': `${m.summaryW}px`,
      '--mx-font': `${m.fontMain}px`,
      '--mx-font-sm': `${m.fontSub}px`,
      '--mx-font-header': `${m.fontHeader}px`,
      '--mx-cell-pad': `${m.cellPad}px`,
    };

    return m;
  }, [viewport]);
}
